import { useSyncExternalStore } from 'react';

import AuthenticationStore from '~/auth/authenticationStore';

import { getRawClientFromUrl } from './utils';

/**
 * Reactive view of the leading workspace/client slug in the URL — the value
 * `AppDataRouter` feeds to `createBrowserRouter` as its `basename`.
 *
 * The slug used to be read once per boot, so moving between a clientless URL
 * (`/login`) and a tenant-scoped one (`/nermal/login`) required a full page
 * load just to rebuild the router with the new basename. Subscribing to it
 * lets `enterClientSlug` swap the basename in place instead.
 */
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Rewrites the URL to `url` and notifies subscribers, so the router remounts
 * against the tenant-scoped path without a page load. Uses `replaceState` to
 * match the `location.replace` this replaced: the clientless URL the user
 * typed is not worth a history entry.
 */
export function enterClientSlug(slug: string, url: string) {
  if (slug === getRawClientFromUrl()) return;

  // A reload used to reset the CSRF token between clients; drop it here now
  // that crossing happens in place, so the new client fetches a fresh one.
  AuthenticationStore().setCsrf(null);

  window.history.replaceState(null, '', url);
  listeners.forEach((listener) => listener());
}

export function useClientSlug() {
  return useSyncExternalStore(subscribe, getRawClientFromUrl);
}
